import React, {useMemo} from 'react';

import {Sequence} from 'components/Sequence';
import {MapType, SEQUENCE_MAP_TYPE_USER_PREFERENCE_KEY} from 'constants/MapsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BulkAssemblyMethod} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {CircularMapTabView} from 'molbio/maps/circular/Circular';
import {SequenceLinearMapTabView} from 'molbio/maps/linear/Linear';
import {MiniMapTabView} from 'molbio/maps/linear/MiniMap';
import {SimplifiedCircularMapTabOptionsView} from 'molbio/maps/options/CircularSequenceOptionsView';
import {SimplifiedLinearMapTabOptionsView} from 'molbio/maps/options/LinearSequenceOptionsView';
import {SimplifiedMiniMapTabOptionsView} from 'molbio/maps/options/MiniMapSequenceOptionsView';
import {
  SEQUENCE_LINEAR_MAP_TAB_STATES,
  SEQUENCE_MINIMAP_TAB_STATES,
} from 'molbio/maps/SequenceMapBackboneConstants';
import PolymerMapWrapper from 'molbio/maps/standalone/PolymerMapWrapper';
import CircularMapTabState from 'molbio/tabstates/CircularMapTabState';
import MiniMapTabState from 'molbio/tabstates/MiniMapTabState';
import {GenericLinearMapTabState} from 'molbio/tabstates/SequenceLinearMapTabState';
import {
  CUTSITES,
  DNA,
  HOMOLOGY_REGION_AXIS,
  HOMOLOGY_REGION_DNA,
  OLIGO_PROPERTIES,
  PRIMERS,
  SEQUENCE_AXIS,
} from 'settings/visibility-options/VisibilityOptions';
import useEventHandler from 'ui-library/hooks/useEventHandler';
import usePreference from 'ui-library/hooks/usePreference';

const MAP_TYPE_TO_VIEW_CLASSES = {
  [MapType.LINEAR_SEQUENCE]: {
    TabOptionsViewCls: SimplifiedLinearMapTabOptionsView,
    TabContentViewCls: SequenceLinearMapTabView,
  },
  [MapType.PLASMID]: {
    TabOptionsViewCls: SimplifiedCircularMapTabOptionsView,
    TabContentViewCls: CircularMapTabView,
  },
  [MapType.MINIMAP]: {
    TabOptionsViewCls: SimplifiedMiniMapTabOptionsView,
    TabContentViewCls: MiniMapTabView,
  },
};

interface MapTabStates {
  [MapType.LINEAR_SEQUENCE]: GenericLinearMapTabState;
  [MapType.PLASMID]?: CircularMapTabState;
  [MapType.MINIMAP]?: MiniMapTabState;
}

export enum AssemblySequenceType {
  FRAGMENT = 'FRAGMENT',
  CONSTRUCT = 'CONSTRUCT',
}

export function getMapTabStates(
  sequence: Sequence,
  bulkAssemblyMethod: BulkAssemblyMethod,
  type: AssemblySequenceType
): MapTabStates {
  const sequenceSubtype = sequence.subtype();
  const mapTabStatesByType: MapTabStates = {
    [MapType.LINEAR_SEQUENCE]: new SEQUENCE_LINEAR_MAP_TAB_STATES[sequenceSubtype]({
      sequence,
    }),
  };
  if (sequence.circular()) {
    mapTabStatesByType[MapType.PLASMID] = new CircularMapTabState({sequence});
  }
  if (
    sequenceSubtype === ModelTypeConstants.SEQUENCE ||
    sequenceSubtype === ModelTypeConstants.RNA_SEQUENCE
  ) {
    mapTabStatesByType[MapType.MINIMAP] = new SEQUENCE_MINIMAP_TAB_STATES[sequenceSubtype]({
      sequence,
    });
  }
  const hasVisibleEnzymes = sequence.get('enzymes').any((e) => e.visible());
  for (const tabState of Object.values(mapTabStatesByType)) {
    // For concatentation assemblies, all enzymes are visible, but the CUTSITES visibility option is False
    // by default by default to avoid cluttering the sequence map.
    // Otherwise, if there are any visible enzymes, then force CUTSITES on.
    // Always show primers.
    if (bulkAssemblyMethod === BulkAssemblyMethod.CONCATENATION) {
      tabState
        .getVisibilityOptions()
        .getOption(CUTSITES.name)
        ?.set({visible: false, editable: true}, {noSave: true});
    } else if (hasVisibleEnzymes) {
      tabState.getVisibilityOptions().getOption(CUTSITES.name)?.set({visible: true}, {noSave: true});
    }
    tabState.getVisibilityOptions().getOption(PRIMERS.name)?.set({visible: true}, {noSave: true});

    // If we're viewing the result of a homology assembly, hide the 'regular' axis and show the homology
    // axis instead.
    if (
      [BulkAssemblyMethod.HOMOLOGY, BulkAssemblyMethod.GIBSON].includes(bulkAssemblyMethod) &&
      type === AssemblySequenceType.CONSTRUCT
    ) {
      if (tabState instanceof CircularMapTabState) {
        tabState
          .getVisibilityOptions()
          .getOption(DNA.name)
          ?.set({visible: false, editable: false}, {noSave: true});

        tabState
          .getVisibilityOptions()
          .getOption(HOMOLOGY_REGION_DNA.name)
          ?.set({visible: true, editable: false}, {noSave: true});
      }
      tabState
        .getVisibilityOptions()
        .getOption(SEQUENCE_AXIS.name)
        ?.set({visible: false, editable: false}, {noSave: true});

      tabState
        .getVisibilityOptions()
        .getOption(HOMOLOGY_REGION_AXIS.name)
        ?.set({visible: true, editable: true}, {noSave: true});
    }
  }

  // Disable and hide the oligo properties banner if it is a visibility option.
  mapTabStatesByType[MapType.LINEAR_SEQUENCE]
    .getVisibilityOptions()
    .getOption(OLIGO_PROPERTIES.name)
    ?.set({sync: false, editable: false, visible: false}, {noSave: true});
  return mapTabStatesByType;
}

export default function SequenceMapContainer({
  sequence,
  className,
  bulkAssemblyMethod,
  type,
}: {
  sequence: Sequence;
  className?: string;
  bulkAssemblyMethod: BulkAssemblyMethod;
  type: AssemblySequenceType;
}): JSX.Element {
  const [sequenceMapTypePref, setSequenceMapTypePref] = usePreference<MapType>(
    SEQUENCE_MAP_TYPE_USER_PREFERENCE_KEY
  );
  const tabStates: MapTabStates = useMemo(
    () => getMapTabStates(sequence, bulkAssemblyMethod, type),
    [sequence, bulkAssemblyMethod, type]
  );
  useEventHandler(sequence, 'changeMapType', setSequenceMapTypePref);

  // If preferred maptype is not available, then fall back to the linear sequence map which is always applicable.
  const mapType = tabStates[sequenceMapTypePref] == null ? MapType.LINEAR_SEQUENCE : sequenceMapTypePref;
  return (
    <PolymerMapWrapper
      className={className}
      {...MAP_TYPE_TO_VIEW_CLASSES[mapType]}
      tabState={tabStates[mapType]}
    />
  );
}
